import React, { useState, useEffect, useCallback } from 'react';
import { collection, getDocs, doc, getDoc, updateDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../firebase/config';
import { Shield, CheckCircle, XCircle, Clock, Building, Phone, Mail, MapPin, FileText, Search, ChevronDown, ChevronUp, ShoppingBag, Wrench, GraduationCap, Trash2, Banknote, Home, Pause, Play } from 'lucide-react';
import { isAdminUser, fetchAdminClaim } from '../utils/adminClaim';

// 업종 타입 정의
const vendorTypeConfig = {
  material_supplier: { label: '재료마켓', icon: ShoppingBag, color: '#8b5cf6' },
  equipment_as: { label: '장비 A/S', icon: Wrench, color: '#f59e0b' },
  education: { label: '교육/세미나', icon: GraduationCap, color: '#10b981' },
  cleaning: { label: '청소/폐기물', icon: Trash2, color: '#6366f1' },
  finance: { label: '금융', icon: Banknote, color: '#ec4899' },
  interior: { label: '인테리어/개원', icon: Home, color: '#14b8a6' },
};

function AdminSellerApproval({ user, userInfo }) {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [filter, setFilter] = useState('pending');
  const [activeTab, setActiveTab] = useState('pending');
  const [vendorTypeFilter, setVendorTypeFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [authChecked, setAuthChecked] = useState(false);

  useEffect(() => {
    const checkAuth = async () => {
      const currentUser = userInfo || user;
      const uid = currentUser?.uid;
      if (!uid) { alert('로그인이 필요합니다.'); window.location.href = '/'; return; }
      try {
        const userDoc = await getDoc(doc(db, 'users', uid));
        if (!userDoc.exists()) { alert('사용자 정보를 찾을 수 없습니다.'); window.location.href = '/'; return; }
        const userData = userDoc.data();
        if (!isAdminUser({ ...userData, adminClaim: await fetchAdminClaim(auth.currentUser) })) { alert('관리자만 접근할 수 있습니다.'); window.location.href = '/dashboard'; return; }
        setAuthChecked(true);
      } catch (error) { console.error('권한 확인 실패:', error); alert('권한 확인 중 오류가 발생했습니다.'); window.location.href = '/'; }
    };
    checkAuth();
  }, [user, userInfo]);

  const loadApplications = useCallback(async () => {
    if (!authChecked) return;
    try {
      setLoading(true);
      const snapshot = await getDocs(collection(db, 'sellerApplications'));
      const appsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      appsData.sort((a, b) => (b.appliedAt?.toMillis() || 0) - (a.appliedAt?.toMillis() || 0));
      setApplications(appsData);
    } catch (error) { console.error('신청서 로드 실패:', error); }
    finally { setLoading(false); }
  }, [authChecked]);

  useEffect(() => { if (authChecked) loadApplications(); }, [authChecked, loadApplications]);

  // 추천 코드 생성 함수
  const generateReferralCode = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let code = '';
    for (let i = 0; i < 6; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return code;
  };

  const handleApprove = async (application) => {
    if (!window.confirm(`${application.companyName}의 판매자 신청을 승인하시겠습니까?`)) return;
    try {
      setProcessing(true);
      const currentUser = userInfo || user;

      // 추천 코드 생성
      const referralCode = generateReferralCode();

      await updateDoc(doc(db, 'users', application.userId), {
        sellerStatus: 'approved',
        businessType: 'seller',
        vendorType: application.vendorType || 'material_supplier',
        approvedAt: serverTimestamp(),
        approvedBy: currentUser.uid,
        // 추천 시스템 필드 추가
        referralCode: referralCode,
        referralCount: 0,
        referralViews: 0
      });

      // 공개 referralCodes 컬렉션에도 저장 (비로그인 사용자 조회용)
      await setDoc(doc(db, 'referralCodes', referralCode), {
        sellerId: application.userId,
        sellerName: application.companyName || application.ownerName || '파트너사',
        createdAt: serverTimestamp()
      });

      await updateDoc(doc(db, 'sellerApplications', application.id), { status: 'approved', approvedAt: serverTimestamp(), approvedBy: currentUser.uid, updatedAt: serverTimestamp() });
      alert('✅ 승인되었습니다! 추천 코드: ' + referralCode);
      await loadApplications();
    } catch (error) { console.error('승인 실패:', error); alert('승인 중 오류가 발생했습니다.'); }
    finally { setProcessing(false); }
  };

  const handleReject = async (application) => {
    const reason = window.prompt('거부 사유를 입력해주세요:');
    if (!reason) return;
    if (!window.confirm(`${application.companyName}의 판매자 신청을 거부하시겠습니까?`)) return;
    try {
      setProcessing(true);
      const currentUser = userInfo || user;
      await updateDoc(doc(db, 'users', application.userId), { sellerStatus: 'rejected', rejectedAt: serverTimestamp(), rejectedBy: currentUser.uid, rejectionReason: reason });
      await updateDoc(doc(db, 'sellerApplications', application.id), { status: 'rejected', rejectedAt: serverTimestamp(), rejectedBy: currentUser.uid, rejectionReason: reason, updatedAt: serverTimestamp() });
      alert('❌ 거부되었습니다.');
      await loadApplications();
    } catch (error) { console.error('거부 실패:', error); alert('거부 중 오류가 발생했습니다.'); }
    finally { setProcessing(false); }
  };

  const handleSuspend = async (application) => {
    const reason = window.prompt('정지 사유를 입력해주세요:');
    if (!reason) return;
    if (!window.confirm(`${application.companyName}의 판매자를 정지하시겠습니까?`)) return;
    try {
      setProcessing(true);
      const currentUser = userInfo || user;
      await updateDoc(doc(db, 'users', application.userId), {
        sellerStatus: 'suspended',
        suspendedAt: serverTimestamp(),
        suspendedBy: currentUser.uid,
        suspendReason: reason
      });
      await updateDoc(doc(db, 'sellerApplications', application.id), {
        status: 'suspended',
        suspendedAt: serverTimestamp(),
        suspendedBy: currentUser.uid,
        suspendReason: reason,
        updatedAt: serverTimestamp()
      });
      alert('판매자가 정지되었습니다.');
      await loadApplications();
    } catch (error) {
      console.error('정지 실패:', error);
      alert('정지 처리 중 오류가 발생했습니다.');
    } finally {
      setProcessing(false);
    }
  };

  const handleReactivate = async (application) => {
    if (!window.confirm(`${application.companyName}의 판매자를 재활성화하시겠습니까?`)) return;
    try {
      setProcessing(true);
      const currentUser = userInfo || user;
      await updateDoc(doc(db, 'users', application.userId), {
        sellerStatus: 'approved',
        reactivatedAt: serverTimestamp(),
        reactivatedBy: currentUser.uid
      });
      await updateDoc(doc(db, 'sellerApplications', application.id), {
        status: 'approved',
        reactivatedAt: serverTimestamp(),
        reactivatedBy: currentUser.uid,
        updatedAt: serverTimestamp()
      });
      alert('판매자가 재활성화되었습니다.');
      await loadApplications();
    } catch (error) {
      console.error('재활성화 실패:', error);
      alert('재활성화 처리 중 오류가 발생했습니다.');
    } finally {
      setProcessing(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    return timestamp.toDate().toLocaleDateString('ko-KR');
  };

  const filteredApplications = applications.filter(app => {
    if (!searchQuery) return true;
    return app.companyName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
           app.ownerName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
           app.businessNumber?.includes(searchQuery);
  }).filter(app => {
    if (activeTab === 'pending') return app.status === 'pending';
    if (activeTab === 'approved') return app.status === 'approved';
    if (activeTab === 'suspended') return app.status === 'suspended';
    return true;
  }).filter(app => filter === 'all' || app.status === filter)
    .filter(app => vendorTypeFilter === 'all' || app.vendorType === vendorTypeFilter || (!app.vendorType && vendorTypeFilter === 'material_supplier'));

  if (!authChecked || loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner}></div>
        <p style={styles.loadingText}>로딩 중...</p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* 헤더 */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <div style={styles.headerIconBox}>
            <Shield size={20} color="#ffffff" />
          </div>
          <span style={styles.headerTitle}>판매자 승인</span>
        </div>
      </div>

      {/* 3 탭: 신청중 / 활성 / 정지 */}
      <div style={styles.tabContainer}>
        {[
          { key: 'pending', label: '신청중', count: applications.filter(a => a.status === 'pending').length },
          { key: 'approved', label: '활성', count: applications.filter(a => a.status === 'approved').length },
          { key: 'suspended', label: '정지', count: applications.filter(a => a.status === 'suspended').length }
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => { setActiveTab(tab.key); setFilter('all'); }}
            style={activeTab === tab.key ? { ...styles.tabButton, ...styles.tabButtonActive } : styles.tabButton}
          >
            {tab.label} ({tab.count})
          </button>
        ))}
      </div>

      {/* 검색 */}
      <div style={styles.searchSection}>
        <div style={styles.searchBox}>
          <Search size={18} color="#9ca3af" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="회사명, 대표자명, 사업자번호 검색..."
            style={styles.searchInput}
          />
        </div>
      </div>

      {/* 상태 필터 */}
      <div style={styles.filterChips}>
        {[
          { key: 'all', label: `전체 (${applications.length})` },
          { key: 'pending', label: `대기중 (${applications.filter(a => a.status === 'pending').length})` },
          { key: 'approved', label: `승인 (${applications.filter(a => a.status === 'approved').length})` },
          { key: 'rejected', label: `거부 (${applications.filter(a => a.status === 'rejected').length})` }
        ].map(f => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            style={filter === f.key ? { ...styles.filterChip, ...styles.filterChipActive } : styles.filterChip}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* 업종 필터 */}
      <div style={styles.vendorTypeFilterSection}>
        <span style={styles.vendorTypeFilterLabel}>업종:</span>
        <div style={styles.vendorTypeFilters}>
          <button
            onClick={() => setVendorTypeFilter('all')}
            style={vendorTypeFilter === 'all' ? { ...styles.vendorTypeFilterBtn, ...styles.vendorTypeFilterBtnActive } : styles.vendorTypeFilterBtn}
          >
            전체
          </button>
          {Object.entries(vendorTypeConfig).map(([key, config]) => {
            const Icon = config.icon;
            const count = applications.filter(a => a.vendorType === key || (!a.vendorType && key === 'material_supplier')).length;
            return (
              <button
                key={key}
                onClick={() => setVendorTypeFilter(key)}
                style={vendorTypeFilter === key
                  ? { ...styles.vendorTypeFilterBtn, ...styles.vendorTypeFilterBtnActive, backgroundColor: config.color, borderColor: config.color }
                  : { ...styles.vendorTypeFilterBtn, color: config.color, borderColor: `${config.color}50` }}
              >
                <Icon size={14} />
                {config.label} ({count})
              </button>
            );
          })}
        </div>
      </div>

      {/* 목록 */}
      <div style={styles.listContainer}>
        {filteredApplications.length === 0 ? (
          <div style={styles.emptyContainer}>
            <div style={styles.emptyIconBox}>
              <Shield size={40} color="#6366f1" />
            </div>
            <p style={styles.emptyText}>판매자 신청 내역이 없습니다</p>
          </div>
        ) : (
          <div style={styles.applicationList}>
            {filteredApplications.map(app => (
              <SellerCard
                key={app.id}
                application={app}
                onApprove={handleApprove}
                onReject={handleReject}
                onSuspend={handleSuspend}
                onReactivate={handleReactivate}
                processing={processing}
                formatDate={formatDate}
              />
            ))}
          </div>
        )}
      </div>

      <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

function SellerCard({ application, onApprove, onReject, onSuspend, onReactivate, processing, formatDate }) {
  const [expanded, setExpanded] = useState(false);

  // 업종 정보 가져오기
  const vendorType = application.vendorType || 'material_supplier';
  const vendorConfig = vendorTypeConfig[vendorType] || vendorTypeConfig.material_supplier;
  const VendorIcon = vendorConfig.icon;

  return (
    <div style={styles.card}>
      {/* 업종 뱃지 */}
      <div style={{...styles.vendorTypeBadge, backgroundColor: `${vendorConfig.color}15`, color: vendorConfig.color}}>
        <VendorIcon size={14} />
        <span>{vendorConfig.label}</span>
      </div>

      {/* 카드 헤더 */}
      <div style={styles.cardHeader}>
        <div style={styles.cardHeaderLeft}>
          <div style={{...styles.sellerIcon, backgroundColor: `${vendorConfig.color}15`}}>
            <VendorIcon size={20} color={vendorConfig.color} />
          </div>
          <div>
            <h3 style={styles.companyName}>{application.companyName}</h3>
            <p style={styles.businessType}>{application.businessType}</p>
          </div>
        </div>
        {application.status === 'pending' ? (
          <div style={styles.statusPending}><Clock size={14} /><span>대기중</span></div>
        ) : application.status === 'approved' ? (
          <div style={styles.statusApproved}><CheckCircle size={14} /><span>활성</span></div>
        ) : application.status === 'suspended' ? (
          <div style={styles.statusSuspended}><Pause size={14} /><span>정지</span></div>
        ) : (
          <div style={styles.statusRejected}><XCircle size={14} /><span>거부</span></div>
        )}
      </div>

      {/* 기본 정보 */}
      <div style={styles.cardBody}>
        <div style={styles.infoGrid}>
          <div style={styles.infoRow}>
            <Building size={16} color="#64748b" />
            <span>대표자: {application.ownerName}</span>
          </div>
          <div style={styles.infoRow}>
            <FileText size={16} color="#64748b" />
            <span>{application.businessNumber}</span>
          </div>
          <div style={styles.infoRow}>
            <Phone size={16} color="#64748b" />
            <span>{application.contactPhone}</span>
          </div>
          <div style={styles.infoRow}>
            <Mail size={16} color="#64748b" />
            <span>{application.contactEmail}</span>
          </div>
        </div>

        {/* 주소 */}
        <div style={styles.addressBox}>
          <MapPin size={16} color="#64748b" />
          <span>{application.address} {application.detailAddress}</span>
        </div>

        {/* 카테고리 */}
        {application.productCategories?.length > 0 && (
          <div style={styles.categorySection}>
            <span style={styles.categoryLabel}>판매 카테고리:</span>
            <div style={styles.categoryTags}>
              {application.productCategories.map((cat, idx) => (
                <span key={idx} style={styles.categoryTag}>{cat}</span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* 확장 영역 */}
      {expanded && (
        <div style={styles.detailsSection}>
          {/* 서비스 지역 */}
          {application.serviceRegions?.length > 0 && (
            <div style={styles.detailBox}>
              <strong>서비스 가능 지역:</strong>
              <div style={styles.regionTags}>
                {application.serviceRegions.map((region, idx) => (
                  <span key={idx} style={{...styles.regionTag, backgroundColor: `${vendorConfig.color}15`, color: vendorConfig.color}}>{region}</span>
                ))}
              </div>
            </div>
          )}

          {/* 업종별 추가 정보 */}
          {vendorType === 'equipment_as' && (
            <div style={styles.detailBox}>
              <strong>A/S 정보:</strong>
              {application.asVisitFee && <p>출장비: {application.asVisitFee}</p>}
              {application.asResponseTime && <p>대응시간: {
                { same_day: '당일 방문', next_day: '익일 방문', '2_3_days': '2~3일 내', within_week: '1주일 내' }[application.asResponseTime]
              }</p>}
            </div>
          )}

          {vendorType === 'education' && (
            <div style={styles.detailBox}>
              <strong>교육 정보:</strong>
              {application.educationType && <p>진행 방식: {
                { offline: '오프라인', online: '온라인', both: '온/오프라인 병행' }[application.educationType]
              }</p>}
              {application.instructorInfo && <p>강사 소개: {application.instructorInfo}</p>}
            </div>
          )}

          {vendorType === 'cleaning' && application.contractType && (
            <div style={styles.detailBox}>
              <strong>계약 방식:</strong>
              <p>{
                { monthly: '월 정기 계약', yearly: '연간 계약', per_visit: '건별 계약', flexible: '협의 가능' }[application.contractType]
              }</p>
            </div>
          )}

          {vendorType === 'finance' && application.financeLicense && (
            <div style={styles.detailBox}>
              <strong>금융업 허가 정보:</strong>
              <p>{application.financeLicense}</p>
            </div>
          )}

          {vendorType === 'interior' && (
            <div style={styles.detailBox}>
              <strong>시공 정보:</strong>
              {application.portfolioUrl && <p>포트폴리오: <a href={application.portfolioUrl} target="_blank" rel="noopener noreferrer">{application.portfolioUrl}</a></p>}
              {application.projectCount && <p>시공 실적: {application.projectCount}</p>}
            </div>
          )}

          {application.description && (
            <div style={styles.descriptionBox}>
              <strong>회사 소개:</strong>
              <p>{application.description}</p>
            </div>
          )}
          <div style={styles.detailItem}>
            <span>신청일시:</span>
            <span>{formatDate(application.appliedAt)}</span>
          </div>
          {application.approvedAt && (
            <div style={styles.detailItem}>
              <span>승인일시:</span>
              <span>{formatDate(application.approvedAt)}</span>
            </div>
          )}
          {application.rejectionReason && (
            <div style={styles.rejectionBox}>
              <strong>거부 사유:</strong>
              <p>{application.rejectionReason}</p>
            </div>
          )}
        </div>
      )}

      {/* 액션 */}
      <div style={styles.cardActions}>
        <button onClick={() => setExpanded(!expanded)} style={styles.detailBtn}>
          {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          <span>{expanded ? '접기' : '자세히'}</span>
        </button>

        {application.status === 'pending' && (
          <>
            <button onClick={() => onReject(application)} style={styles.rejectBtn} disabled={processing}>
              <XCircle size={16} />
              <span>거부</span>
            </button>
            <button onClick={() => onApprove(application)} style={styles.approveBtn} disabled={processing}>
              <CheckCircle size={16} />
              <span>승인</span>
            </button>
          </>
        )}

        {application.status === 'approved' && (
          <button onClick={() => onSuspend(application)} style={styles.suspendBtn} disabled={processing}>
            <Pause size={16} />
            <span>정지</span>
          </button>
        )}

        {application.status === 'suspended' && (
          <button onClick={() => onReactivate(application)} style={styles.reactivateBtn} disabled={processing}>
            <Play size={16} />
            <span>재활성화</span>
          </button>
        )}
      </div>
    </div>
  );
}

const styles = {
  container: { minHeight: '100vh', backgroundColor: '#f1f5f9' },

  // 헤더
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', backgroundColor: '#6366f1' },

  // 업종 필터
  vendorTypeFilterSection: { padding: '0 14px 14px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' },
  vendorTypeFilterLabel: { fontSize: '13px', fontWeight: '600', color: '#64748b' },
  vendorTypeFilters: { display: 'flex', gap: '8px', flexWrap: 'wrap' },
  vendorTypeFilterBtn: { display: 'flex', alignItems: 'center', gap: '4px', padding: '6px 12px', backgroundColor: '#ffffff', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: '12px', fontWeight: '600', color: '#64748b', cursor: 'pointer', whiteSpace: 'nowrap' },
  vendorTypeFilterBtnActive: { backgroundColor: '#6366f1', borderColor: '#6366f1', color: '#ffffff' },

  // 업종 뱃지
  vendorTypeBadge: { display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: '600', marginBottom: '10px' },

  // 서비스 지역 태그
  regionTags: { display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px' },
  regionTag: { padding: '4px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: '500' },

  // 상세 박스
  detailBox: { marginBottom: '12px', fontSize: '13px', color: '#475569' },
  headerLeft: { display: 'flex', alignItems: 'center', gap: '10px' },
  headerIconBox: { width: '36px', height: '36px', borderRadius: '10px', backgroundColor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  headerTitle: { fontSize: '20px', fontWeight: '700', color: '#ffffff' },

  // 검색
  searchSection: { padding: '14px' },
  searchBox: { display: 'flex', alignItems: 'center', gap: '10px', padding: '12px', backgroundColor: '#ffffff', borderRadius: '10px' },
  searchInput: { flex: 1, border: 'none', outline: 'none', fontSize: '14px', color: '#0f172a' },

  // 필터
  filterChips: { display: 'flex', gap: '8px', padding: '0 14px 14px', overflowX: 'auto' },
  filterChip: { padding: '8px 14px', backgroundColor: '#ffffff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '600', color: '#64748b', cursor: 'pointer', whiteSpace: 'nowrap' },
  filterChipActive: { backgroundColor: '#6366f1', color: '#ffffff' },

  // 목록
  listContainer: { padding: '0 14px 14px' },
  applicationList: { display: 'flex', flexDirection: 'column', gap: '10px' },
  card: { backgroundColor: '#ffffff', borderRadius: '14px', padding: '14px' },
  cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' },
  cardHeaderLeft: { display: 'flex', alignItems: 'center', gap: '12px' },
  sellerIcon: { width: '44px', height: '44px', borderRadius: '12px', backgroundColor: '#eef2ff', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  companyName: { margin: 0, fontSize: '16px', fontWeight: '700', color: '#0f172a' },
  businessType: { margin: '2px 0 0', fontSize: '12px', color: '#64748b' },

  // 상태 뱃지
  statusPending: { display: 'flex', alignItems: 'center', gap: '4px', padding: '5px 10px', backgroundColor: '#fef3c7', borderRadius: '8px', fontSize: '12px', fontWeight: '600', color: '#92400e' },
  statusApproved: { display: 'flex', alignItems: 'center', gap: '4px', padding: '5px 10px', backgroundColor: '#d1fae5', borderRadius: '8px', fontSize: '12px', fontWeight: '600', color: '#065f46' },
  statusRejected: { display: 'flex', alignItems: 'center', gap: '4px', padding: '5px 10px', backgroundColor: '#fee2e2', borderRadius: '8px', fontSize: '12px', fontWeight: '600', color: '#991b1b' },

  // 카드 바디
  cardBody: { display: 'flex', flexDirection: 'column', gap: '12px' },
  infoGrid: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px' },
  infoRow: { display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#475569' },
  addressBox: { display: 'flex', alignItems: 'flex-start', gap: '8px', padding: '12px', backgroundColor: '#f8fafc', borderRadius: '8px', fontSize: '13px', color: '#475569' },
  categorySection: { fontSize: '13px' },
  categoryLabel: { fontWeight: '600', color: '#475569' },
  categoryTags: { display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px' },
  categoryTag: { padding: '4px 10px', backgroundColor: '#e0e7ff', color: '#6366f1', fontSize: '12px', fontWeight: '600', borderRadius: '6px' },

  // 확장 영역
  detailsSection: { padding: '14px', backgroundColor: '#f8fafc', borderRadius: '10px', marginTop: '12px' },
  descriptionBox: { marginBottom: '12px', fontSize: '13px', color: '#475569' },
  detailItem: { display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #e2e8f0', fontSize: '13px', color: '#475569' },
  rejectionBox: { padding: '12px', backgroundColor: '#fee2e2', borderRadius: '8px', marginTop: '12px', fontSize: '13px', color: '#991b1b' },

  // 액션
  cardActions: { display: 'flex', gap: '8px', marginTop: '14px' },
  detailBtn: { display: 'flex', alignItems: 'center', gap: '4px', padding: '10px 14px', backgroundColor: '#f3f4f6', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '600', color: '#64748b', cursor: 'pointer', marginRight: 'auto' },
  rejectBtn: { display: 'flex', alignItems: 'center', gap: '4px', padding: '10px 14px', backgroundColor: '#ef4444', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '600', color: '#ffffff', cursor: 'pointer' },
  approveBtn: { display: 'flex', alignItems: 'center', gap: '4px', padding: '10px 14px', backgroundColor: '#10b981', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '600', color: '#ffffff', cursor: 'pointer' },

  // 빈 상태
  emptyContainer: { display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '60px 20px' },
  emptyIconBox: { width: '80px', height: '80px', borderRadius: '40px', backgroundColor: '#eef2ff', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '16px' },
  emptyText: { fontSize: '16px', fontWeight: '600', color: '#374151', margin: 0 },

  // 탭
  tabContainer: { display: 'flex', gap: '0', padding: '0 14px', marginBottom: '14px' },
  tabButton: { flex: 1, padding: '12px 16px', backgroundColor: '#ffffff', border: '2px solid #e2e8f0', borderRadius: '0', fontSize: '14px', fontWeight: '600', color: '#64748b', cursor: 'pointer', textAlign: 'center', transition: 'all 0.2s' },
  tabButtonActive: { backgroundColor: '#6366f1', borderColor: '#6366f1', color: '#ffffff' },

  // 정지 상태
  statusSuspended: { display: 'flex', alignItems: 'center', gap: '4px', padding: '5px 10px', backgroundColor: '#fef3c7', borderRadius: '8px', fontSize: '12px', fontWeight: '600', color: '#92400e' },

  // 정지/재활성화 버튼
  suspendBtn: { display: 'flex', alignItems: 'center', gap: '4px', padding: '10px 14px', backgroundColor: '#f59e0b', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '600', color: '#ffffff', cursor: 'pointer' },
  reactivateBtn: { display: 'flex', alignItems: 'center', gap: '4px', padding: '10px 14px', backgroundColor: '#10b981', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '600', color: '#ffffff', cursor: 'pointer' },

  // 로딩
  loadingContainer: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '400px', gap: '16px' },
  spinner: { width: '40px', height: '40px', border: '4px solid #e2e8f0', borderTop: '4px solid #6366f1', borderRadius: '50%', animation: 'spin 1s linear infinite' },
  loadingText: { fontSize: '14px', color: '#64748b' },
};

export default AdminSellerApproval;